/**
 * assumptionLedger.js v1.0.0 - 2026-10-09
 *
 * "Assumptions used": every time the calculation fills in a missing planner input with a documented
 * default (constants/planningDefaults.js), the use is recorded here. casualtyCalculator resets the
 * ledger at the start of a run and attaches the list to results.assumptions; Tab 5 displays it.
 *
 * CALC-010: replaces silent `value || <literal>` fallbacks. The calculation result is the same as before
 * for a given default; what changes is that the planner can see which inputs were assumed.
 *
 * Version history:
 *   v1.0.0 (2026-10-09): initial release
 */
import { PLANNING_DEFAULTS } from '../constants/planningDefaults.js';

let entries = new Map();

export const assumptionLedger = {
  reset() {
    entries = new Map();
  },

  /** Record that the default for `id` was used. Repeated use counts once per id. */
  note(id, extra = {}) {
    const def = PLANNING_DEFAULTS[id];
    if (!def) {
      throw new Error(`assumptionLedger.note: unknown planning default '${id}' (add it to constants/planningDefaults.js)`);
    }
    const existing = entries.get(id);
    if (existing) {
      existing.uses += 1;
      return;
    }
    entries.set(id, {
      id,
      label: def.label,
      assumed_value: def.value,
      unit: def.unit,
      set_in: def.set_in,
      source: def.source,
      uses: 1,
      ...extra
    });
  },

  list() {
    return Array.from(entries.values());
  }
};

/**
 * Return `value` when the planner supplied it; otherwise return the documented default and record it.
 *
 * @param {*} value - the input as read from the scenario (may be undefined/null/NaN)
 * @param {string} id - key of PLANNING_DEFAULTS
 * @param {Object} [options]
 * @param {boolean} [options.zeroIsMissing=false] - treat 0 / '' as missing too (use where the old code used `||`)
 */
export function resolveInput(value, id, options = {}) {
  const def = PLANNING_DEFAULTS[id];
  if (!def) {
    throw new Error(`resolveInput: unknown planning default '${id}' (add it to constants/planningDefaults.js)`);
  }
  const { zeroIsMissing = false } = options;
  const missing =
    value === undefined || value === null || (typeof value === 'number' && Number.isNaN(value)) ||
    (zeroIsMissing && (value === 0 || value === ''));
  if (!missing) return value;
  assumptionLedger.note(id);
  return def.value;
}
